import { useState, useEffect, useCallback } from 'react';
import { useSocket } from './useSocket';
import { useAuth } from './useAuth';
import { Order } from '../types';
import { ordersAPI } from '../services/api';

interface UseOrderAcceptanceReturn {
  pendingOrder: Order | null;
  isModalOpen: boolean;
  isAutoAcceptEnabled: boolean;
  acceptOrder: (estimatedTime: number) => Promise<void>;
  rejectOrder: () => Promise<void>;
  closeModal: () => void;
  acceptOrderById: (orderId: number, estimatedTime: number) => Promise<void>;
  rejectOrderById: (orderId: number, reason?: string) => Promise<void>;
}

const useOrderAcceptance = (): UseOrderAcceptanceReturn => {
  const [pendingOrder, setPendingOrder] = useState<Order | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAutoAcceptEnabled, setIsAutoAcceptEnabled] = useState(true);
  const { socket, isConnected } = useSocket();
  const { store, platforms } = useAuth();

  // Check if auto-accept is enabled for Efood
  useEffect(() => {
    if (platforms && platforms.length > 0) {
      const efoodPlatform = platforms.find(p => p.platform_name === 'efood');
      if (efoodPlatform) {
        setIsAutoAcceptEnabled(efoodPlatform.auto_accept_orders);
      }
    }
  }, [platforms]);

  // Handle new order notifications
  useEffect(() => {
    if (!socket || !isConnected || !store) return;

    const handleNewOrder = (order: Order) => {
      // Orders with api_status='pending' will show inline controls based on database state
      if (order.platform_name === 'efood' && !isAutoAcceptEnabled && order.status === 'pending') {
        // No longer showing modal - will use inline controls instead
      }
    };

    // Handle order status changes
    const handleOrderStatusChange = (data: { orderId: number; status: string; order?: Order }) => {
      // Close modal if the pending order was processed
      if (pendingOrder && pendingOrder.id === data.orderId) {
        if (data.status === 'accepted' || data.status === 'rejected') {
          setIsModalOpen(false);
          setPendingOrder(null);
        }
      }
    };

    // Register event listeners
    socket.on('new-order', handleNewOrder);
    socket.on('order-status-changed', handleOrderStatusChange);

    // Cleanup
    return () => {
      socket.off('new-order', handleNewOrder);
      socket.off('order-status-changed', handleOrderStatusChange);
    };
  }, [socket, isConnected, store, isAutoAcceptEnabled, pendingOrder]);

  // Accept order with estimated time
  const acceptOrder = useCallback(async (estimatedTime: number) => {
    if (!pendingOrder || !store) {
      throw new Error('No pending order or store not found');
    }

    try {
      await ordersAPI.acceptOrder(pendingOrder.id, estimatedTime);
      
      // Close modal
      setIsModalOpen(false);
      setPendingOrder(null);
    } catch (error) {
      console.error('❌ [ORDER ACCEPTANCE] Failed to accept order:', error);
      throw error;
    }
  }, [pendingOrder, store]);

  // Reject order
  const rejectOrder = useCallback(async () => {
    if (!pendingOrder || !store) {
      throw new Error('No pending order or store not found');
    }

    try {
      await ordersAPI.rejectOrder(pendingOrder.id);
      
      // Close modal
      setIsModalOpen(false);
      setPendingOrder(null);
    } catch (error) {
      console.error('❌ [ORDER ACCEPTANCE] Failed to reject order:', error);
      throw error;
    }
  }, [pendingOrder, store]);

  // Close modal without action
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    // Keep pendingOrder in case user wants to reopen
  }, []);

  // Accept order by ID (for inline acceptance)
  const acceptOrderById = useCallback(async (orderId: number, estimatedTime: number) => {
    if (!store) {
      throw new Error('Store not found');
    }

    try {
      await ordersAPI.acceptOrder(orderId, estimatedTime);
    } catch (error: any) {
      // InlineOrderAcceptance surfaces the platform's message and refreshes the
      // list (via onStale) on a no-longer-acceptable order — just log + rethrow
      // so we don't double-alert.
      console.error('❌ [ORDER ACCEPTANCE] Failed to accept order:', error);
      throw error;
    }
  }, [store]);

  // Reject order by ID (for inline rejection)
  const rejectOrderById = useCallback(async (orderId: number, reason?: string) => {
    if (!store) {
      throw new Error('Store not found');
    }

    try {
      await ordersAPI.rejectOrder(orderId, reason);
    } catch (error) {
      console.error('❌ [ORDER ACCEPTANCE] Failed to reject order:', error);
      throw error;
    }
  }, [store]);

  return {
    pendingOrder,
    isModalOpen,
    isAutoAcceptEnabled,
    acceptOrder,
    rejectOrder,
    closeModal,
    acceptOrderById,
    rejectOrderById
  };
};

export default useOrderAcceptance;

